import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Row,
  Modal,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, StopOutlined } from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import { getRazonesDeRechazo } from 'api/catalogos';
import { cancelarTramite, cancelarTramiteDePortal } from 'api/tramites/tramites';
import { procedenciasEnum, tipoDeDocumentoExpedienteEnum } from 'views/Tramites/GPM/enums';
import { postExpedienteDeTramite } from 'api/tramites/expediente';
import Uploader from 'components/Uploader';

const ModalCancelarTramite = ({
  visible,
  setVisible,
  callback,
  tramite,
  cargos,
  cancelarPadron,
  cancelarCargos,
  tipoDePadron,
  esRefrendo,
  tramiteDePortal,
  placa,
  limpiarJson,
  limpiarJsonPrediosRelacionados,
}) => {
  const [form] = Form.useForm();
  const razon_de_rechazo = Form.useWatch('razon_de_rechazo', form);
  const reembolso = Form.useWatch('reembolso', form);
  const [razones, setRazones] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _razones = await getRazonesDeRechazo({ es_razon_rechazo_ciudadano: false });
      if (mounted) {
        setRazones(_razones);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const cargosGenerados = cargos.filter((c) => c.procedencia === procedenciasEnum.GPM);
  const cargosLigados = cargos.filter((c) => c.procedencia !== procedenciasEnum.GPM);
  const allGeneradosSaldados = !!cargosGenerados.length && cargosGenerados.every((c) => c.saldado);
  const generadosSaldados = !!cargosGenerados.length
    && (allGeneradosSaldados || cargosGenerados.some((c) => c.saldado));
  const ligadosSaldados = !!cargosLigados.length && cargosLigados.some((c) => c.saldado);

  const razon = razones.find((r) => r.id === razon_de_rechazo);

  const onFinish = async () => {
    const { archivo, ...values } = form.getFieldsValue();
    values.tramite = tramite;
    values.cancelar_padron = cancelarPadron;
    values.cancelar_cargos = cancelarCargos || reembolso;
    values.es_refrendo = esRefrendo;
    values.placa = placa;
    values.limpiar_json = limpiarJson;
    values.limpiar_json_predios_relacionados = limpiarJsonPrediosRelacionados;
    // values.cancelar_cargos = (cancelarCargos && !generadosSaldados) || reembolso;
    // dejar como esta por ahora, esta cancelando cargos saldados y recibos cuando no debería,
    // una vez revisado avisar que para los tramites de pruebas deben ser eliminados los recibos,
    // si realmente se realizó la transacción hacer el reembolso
    const canceled = (tramiteDePortal
      ? await cancelarTramiteDePortal(values) : await cancelarTramite(values));
    if (canceled) {
      if (archivo) {
        const fileName = `anexo cancelacion.${archivo.file.name.split('.')[1]}`;
        archivo.file = new File([archivo.file], fileName, { type: archivo.file.type });
        const valuesArchivo = {
          tramite,
          tipo_de_documento_para_expediente: tipoDeDocumentoExpedienteEnum.ANEXO_CANCELACION,
          archivo: archivo.file,
        };
        await postExpedienteDeTramite(valuesArchivo);
      }
      callback(canceled);
      setVisible(false);
      message.info('Trámite cancelado correctamente');
    }
    form.resetFields();
  };

  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => {
        setVisible(false);
      },
    },
    {
      text: 'Cancelar',
      icon: <StopOutlined />,
      onClick: () => form.submit(),
      danger: true,
      className: 'no-color',
    },
  ];

  const rules = { required: [{ required: true }] };

  return (
    <Modal
      footer={null}
      closable={false}
      visible={visible}
      title={<FormSubmitControls label="Cancelar Trámite" controls={controls} />}
    >
      <Row>
        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            name="cancelar_tramite"
            onFinish={() => onFinish()}
            initialValues={{ reembolso: false }}
          >
            <Col span={24}>
              <Form.Item
                label="Comentarios"
                name="comentario_de_rechazo"
                rules={rules.required}
              >
                <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} allowClear />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Razón de Rechazo"
                name="razones_de_rechazo"
                rules={rules.required}
              >
                <Select dataSource={razones} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Uploader
                title="Archivo adjunto"
                formItemName="archivo"
                rules={[{ required: !!razon?.necesita_archivo }]}
                allowedExtensions={['pdf', 'docx']}
                limitMB={5}
                fullSize
              />
            </Col>
            {!cancelarCargos && (
              <>
                <Col span={24}>
                  <Form.Item
                    label="¿Hacer reembolso?"
                    name="reembolso"
                    rules={rules.required}
                    tooltip={!generadosSaldados && !ligadosSaldados
                      ? 'No hay pagos ligados al trámite' : ''}
                  >
                    <Select trueFalse disabled={!generadosSaldados && !ligadosSaldados} />
                  </Form.Item>
                </Col>
                <Typography.Text type="warning">
                  {!!cargosGenerados.length && !allGeneradosSaldados && (
                    <>
                      Los cargos generados mediante el trámite (no saldados)
                      serán cancelados.
                      <br />
                    </>
                  )}
                  {reembolso && generadosSaldados && (
                    <>
                      Los cargos saldados que han sido generados mediante el trámite
                      serán cancelados, de igual manera el recibo generado.
                      <br />
                    </>
                  )}
                  {!!cargosLigados.length && (
                    <>
                      Los cargos ligados al trámite pero generados por otros medios
                      {' '}
                      {ligadosSaldados
                        ? 'serán restablecidos'
                        : 'solo dejaran de estar relacionados a este trámite'}
                      .
                    </>
                  )}
                </Typography.Text>
              </>
            )}
            <br />
            {cancelarPadron && (
              <Typography.Text type="danger">
                {`* El padrón ${tipoDePadron} registrado durante el trámite será eliminado con esta acción`}
              </Typography.Text>
            )}
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

ModalCancelarTramite.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  tramite: PropTypes.number,
  cancelarPadron: PropTypes.bool,
  cancelarCargos: PropTypes.bool,
  esRefrendo: PropTypes.bool,
  tipoDePadron: PropTypes.string,
  cargos: PropTypes.arrayOf(PropTypes.shape()),
  tramiteDePortal: PropTypes.bool,
  placa: PropTypes.string,
  limpiarJson: PropTypes.bool,
  limpiarJsonPrediosRelacionados: PropTypes.bool,
};

ModalCancelarTramite.defaultProps = {
  tramite: null,
  cancelarPadron: false,
  cancelarCargos: false,
  esRefrendo: false,
  tipoDePadron: null,
  cargos: [],
  tramiteDePortal: false,
  placa: null,
  limpiarJson: false,
  limpiarJsonPrediosRelacionados: false,
};

export default ModalCancelarTramite;
